/* Cleanlynk theme — shared behaviour */
(() => {
  const theme = (window.theme = window.theme || {});
  const routes = theme.routes || {};

  /* Helpers ---------------------------------------------------------------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, wait) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };
  theme.debounce = debounce;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  /* Toast ------------------------------------------------------------------ */
  let toastTimer;
  theme.toast = (message) => {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3200);
  };

  /* Light / dark toggle ---------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-theme-toggle]');
    if (!btn) return;
    const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('cl-theme', next); } catch (err) {}
    btn.setAttribute('aria-pressed', next === 'dark' ? 'true' : 'false');
  });

  /* Accordions inside menus ------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-accordion]');
    if (!btn) return;
    const target = document.getElementById(btn.getAttribute('aria-controls'));
    const open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
    if (target) target.hidden = open;
  });

  /* Drawers ---------------------------------------------------------------- */
  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = qs('[data-drawer-panel]', this) || qs('.drawer__panel', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      if (this.dataset.mobileOnly !== undefined || this.tagName === 'FACETS-DRAWER') {
        this.mq = window.matchMedia(this.closest('.filters--drawer') ? 'all' : '(max-width: 989px)');
        const sync = () => { if (!this.classList.contains('is-open')) { if (this.mq.matches) this.setAttribute('aria-hidden', 'true'); else this.removeAttribute('aria-hidden'); } };
        this.mq.addEventListener('change', sync);
        sync();
      }
      if (this.id) {
        document.addEventListener('click', (e) => {
          const opener = e.target.closest(`[data-drawer-open="${this.id}"]`);
          if (opener) { e.preventDefault(); this.open(opener); }
        });
      }
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      this.releaseTrap = trapFocus(this.panel);
      setTimeout(() => (qs('[autofocus]', this.panel) || this.panel).focus(), 50);
    }
    hidesWhenClosed() { return !this.mq || this.mq.matches; }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      if (this.hidesWhenClosed()) this.setAttribute('aria-hidden', 'true');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      if (this.releaseTrap) this.releaseTrap();
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }
  ['menu-drawer', 'cart-drawer', 'facets-drawer'].forEach((name) => {
    if (!customElements.get(name)) customElements.define(name, class extends ThemeDrawer {});
  });

  /* Add to cart pop-up: shows only the line(s) just added ------------------ */
  if (!customElements.get('cart-notification')) {
    customElements.define('cart-notification', class extends ThemeDrawer {
      show(data) {
        const items = data && Array.isArray(data.items) ? data.items : [data];
        const keys = items.map((i) => i && i.key).filter(Boolean);
        const lines = qsa('[data-popup-line]', this);
        let shown = 0;
        lines.forEach((el) => {
          const match = keys.includes(el.dataset.popupLine) && shown < 2;
          el.hidden = !match;
          if (match) shown += 1;
        });
        if (!shown && lines[0]) { lines[0].hidden = false; shown = 1; }
        const more = qs('[data-popup-more]', this);
        const extra = keys.length - shown;
        if (more) {
          more.hidden = extra < 1;
          if (extra > 0) more.textContent = extra === 1 ? theme.strings.popupMoreOne.replace('[count]', extra) : theme.strings.popupMoreOther.replace('[count]', extra);
        }
        if (this.classList.contains('is-open')) return;
        this.open(document.activeElement);
      }
    });
  }

  /* Inc / Ex VAT switch ---------------------------------------------------- */
  theme.vatMode = () => (document.documentElement.classList.contains('ex-vat') ? 'ex' : 'inc');
  const syncVat = () => {
    const mode = theme.vatMode();
    qsa('vat-toggle [data-vat]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.vat === mode)));
  };
  if (!customElements.get('vat-toggle')) {
    customElements.define('vat-toggle', class extends HTMLElement {
      connectedCallback() {
        syncVat();
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-vat]');
          if (!btn) return;
          document.documentElement.classList.toggle('ex-vat', btn.dataset.vat === 'ex');
          try { localStorage.setItem('cl-vat', btn.dataset.vat); } catch (err) {}
          syncVat();
          document.dispatchEvent(new CustomEvent('vat:change', { detail: { mode: btn.dataset.vat } }));
        });
      }
    });
  }

  /* Money formatting (shopper's currency) --------------------------------- */
  theme.formatMoney = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || theme.currency || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency }).format(cents / 100);
    } catch (e) { return (cents / 100).toFixed(2); }
  };
  const fill = (str, vars) => Object.keys(vars).reduce((out, k) => out.split(`[${k}]`).join(vars[k]), str || '');

  /* Pop-up dialogs (finance info, quote form) ------------------------------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      if (dialog.hasAttribute('data-quote-dialog') && opener.dataset.quoteType) {
        const req = qs('[data-quote-request]', dialog);
        if (req) req.value = opener.dataset.quoteType;
        const heading = qs('[data-quote-heading]', dialog);
        if (heading) heading.textContent = opener.dataset.quoteTitle;
        const best = qs('[data-quote-best-time]', dialog);
        if (best) {
          const isCall = opener.querySelector('.icon-phone') !== null;
          best.hidden = !isCall;
          qsa('select', best).forEach((sel) => { sel.disabled = !isCall; });
        }
      }
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      return;
    }
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) { const d = closer.closest('dialog'); if (d) d.close(); return; }
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('modal')) e.target.close();
  });
  document.addEventListener('close', (e) => {
    if (e.target.tagName === 'DIALOG' && !qs('dialog[open]')) document.documentElement.style.overflow = '';
  }, true);
  // After a quote form is sent (or has errors), the page reloads: show the result straight away.
  qsa('[data-quote-dialog]').forEach((d) => {
    if (qs('[data-quote-done], [data-quote-error]', d) && typeof d.showModal === 'function') {
      d.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
  });

  /* Finance message --------------------------------------------------------- */
  if (!customElements.get('finance-message')) {
    customElements.define('finance-message', class extends HTMLElement {
      connectedCallback() {
        const price = Number(this.dataset.price) || 0;
        const provider = this.dataset.provider || '';
        const s = theme.strings;
        let text;
        if (this.dataset.mode === 'credit') {
          const term = Number(this.dataset.term) || 36;
          const apr = parseFloat(String(this.dataset.apr).replace('%', '')) || 0;
          const deposit = Math.round(price * (Number(this.dataset.deposit) || 0) / 100);
          const credit = price - deposit;
          const r = Math.pow(1 + apr / 100, 1 / 12) - 1;
          const monthly = Math.ceil(r > 0 ? (credit * r) / (1 - Math.pow(1 + r, -term)) : credit / term);
          const total = monthly * term + deposit;
          text = fill(s.financeCredit, { amount: theme.formatMoney(monthly), provider });
          const ex = s.financeExample;
          const rows = [
            [ex.cash, theme.formatMoney(price)],
            deposit > 0 ? [ex.deposit, theme.formatMoney(deposit)] : null,
            [ex.credit, theme.formatMoney(credit)],
            [ex.term, fill(ex.months, { count: term })],
            [ex.monthly, theme.formatMoney(monthly)],
            [ex.total, theme.formatMoney(total)],
            [ex.apr, `${apr}% ${ex.fixed}`]
          ].filter(Boolean);
          const dl = qs('[data-finance-example]', this);
          if (dl) {
            dl.innerHTML = '';
            const cap = document.createElement('p');
            cap.className = 'finance__example-heading';
            cap.textContent = ex.heading;
            dl.appendChild(cap);
            rows.forEach(([k, v]) => {
              const dt = document.createElement('dt'); dt.textContent = k;
              const dd = document.createElement('dd'); dd.textContent = v;
              dl.append(dt, dd);
            });
            dl.hidden = false;
          }
        } else {
          const count = Number(this.dataset.instalments) || 3;
          text = fill(s.financeSplit, { count, amount: theme.formatMoney(Math.ceil(price / count)), provider });
        }
        qsa('[data-finance-text]', this).forEach((el) => { el.textContent = text; });
      }
    });
  }

  /* Price markup that follows the VAT switch (matches snippets/money.liquid) */
  theme.priceHTML = (cents, taxable = true) => {
    const inc = theme.formatMoney(cents);
    if (!theme.vat) return inc;
    const ex = taxable ? Math.round((cents * 100) / (100 + Number(theme.vat.rate))) : cents;
    return `<span class="m-inc">${inc}</span><span class="m-ex">${theme.formatMoney(ex)}</span>`;
  };

  /* System builder ---------------------------------------------------------- */
  if (!customElements.get('system-builder')) {
    customElements.define('system-builder', class extends HTMLElement {
      connectedCallback() {
        this.steps = qsa('[data-step]', this);
        this.addEventListener('change', (e) => {
          if (e.target.matches('[data-variant-select]')) this.applyVariant(e.target);
          else if (e.target.matches('[data-option], [data-none]')) this.onPick(e.target);
          this.update();
        });
        this.addEventListener('click', (e) => {
          const jump = e.target.closest('[data-jump]');
          if (jump && this.steps[jump.dataset.jump]) this.steps[jump.dataset.jump].scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        qsa('[data-builder-add]', this).forEach((b) => b.addEventListener('click', () => this.add(b)));
        this.update();
      }
      applyVariant(select) {
        const opt = select.selectedOptions[0];
        const input = qs(`[data-select="${select.id}"]`, this);
        if (!opt || !input) return;
        input.value = opt.value;
        input.dataset.price = opt.dataset.price;
        input.dataset.taxable = opt.dataset.taxable;
        input.dataset.variantTitle = opt.dataset.title;
        const price = qs('[data-option-price]', input.closest('label'));
        if (price) price.innerHTML = theme.priceHTML(Number(opt.dataset.price), opt.dataset.taxable !== 'false');
        if (!input.checked) { input.checked = true; this.onPick(input); }
      }
      onPick(input) {
        if (input.type !== 'radio') return;
        const step = input.closest('[data-step]');
        const next = this.steps[this.steps.indexOf(step) + 1];
        if (!next || input.matches('[data-none]')) return;
        const rect = next.getBoundingClientRect();
        if (rect.top > window.innerHeight * 0.75) {
          window.scrollBy({ top: rect.top - window.innerHeight * 0.3, behavior: 'smooth' });
        }
      }
      chosen() { return qsa('[data-option]:checked', this); }
      update() {
        const chosen = this.chosen();
        let total = 0;
        let totalEx = 0;
        const rate = theme.vat ? Number(theme.vat.rate) : 0;
        const list = qs('[data-summary-list]', this);
        list.innerHTML = '';
        chosen.forEach((input) => {
          const price = Number(input.dataset.price) || 0;
          const taxable = input.dataset.taxable !== 'false';
          total += price;
          totalEx += taxable ? Math.round((price * 100) / (100 + rate)) : price;
          const step = input.closest('[data-step]');
          const li = document.createElement('li');
          li.className = 'builder-summary__item';
          const name = document.createElement('button');
          name.type = 'button';
          name.className = 'builder-summary__name';
          name.dataset.jump = String(this.steps.indexOf(step));
          name.textContent = input.dataset.title;
          if (input.dataset.variantTitle) {
            const sub = document.createElement('span');
            sub.className = 'muted small';
            sub.textContent = input.dataset.variantTitle;
            name.appendChild(sub);
          }
          const p = document.createElement('span');
          p.className = 'builder-summary__price';
          p.innerHTML = theme.priceHTML(price, taxable);
          li.append(name, p);
          list.appendChild(li);
        });
        if (!chosen.length) {
          const li = document.createElement('li');
          li.className = 'muted small';
          li.textContent = theme.strings.builderEmpty;
          list.appendChild(li);
        }
        const totalHTML = theme.vat
          ? `<span class="m-inc">${theme.formatMoney(total)}</span><span class="m-ex">${theme.formatMoney(totalEx)}</span>`
          : theme.formatMoney(total);
        qsa('[data-summary-total]', this).forEach((el) => { el.innerHTML = totalHTML; });

        const missing = [];
        this.steps.forEach((step) => {
          const done = qs('[data-option]:checked', step) !== null;
          const skipped = qs('[data-none]:checked', step) !== null;
          step.classList.toggle('is-done', done || (skipped && step.dataset.required !== 'true'));
          if (step.dataset.required === 'true' && !done) missing.push(step.dataset.title);
        });
        const msg = qs('[data-summary-missing]', this);
        msg.hidden = missing.length === 0;
        if (missing.length) msg.textContent = theme.strings.builderMissing.split('[steps]').join(missing.join(', '));
        qsa('[data-builder-add]', this).forEach((b) => { b.disabled = missing.length > 0 || chosen.length === 0; });
      }
      async add(btn) {
        const chosen = this.chosen();
        if (!chosen.length) return;
        const fd = new FormData();
        chosen.forEach((input, i) => {
          fd.append(`items[${i}][id]`, input.value);
          fd.append(`items[${i}][quantity]`, '1');
          if (input.dataset.lead) fd.append(`items[${i}][properties][${theme.strings.leadTimeName}]`, input.dataset.lead);
          if (this.dataset.buildLabel) fd.append(`items[${i}][properties][_build]`, this.dataset.buildLabel);
        });
        qsa('[data-builder-add]', this).forEach((b) => b.classList.add('is-loading'));
        try { await theme.cart.add(fd); }
        catch (err) { theme.toast(err.message || theme.strings.error); }
        finally { qsa('[data-builder-add]', this).forEach((b) => b.classList.remove('is-loading')); }
      }
    });
  }

  /* Recently viewed --------------------------------------------------------- */
  const RECENT_PRODUCTS = 'cl-recent-products';
  const readRecentProducts = () => { try { return JSON.parse(localStorage.getItem(RECENT_PRODUCTS)) || []; } catch (e) { return []; } };
  if (document.body.dataset.productHandle) {
    const handle = document.body.dataset.productHandle;
    const list = [handle, ...readRecentProducts().filter((h) => h !== handle)].slice(0, 12);
    try { localStorage.setItem(RECENT_PRODUCTS, JSON.stringify(list)); } catch (e) {}
  }
  if (!customElements.get('recently-viewed')) {
    customElements.define('recently-viewed', class extends HTMLElement {
      connectedCallback() {
        const exclude = this.dataset.exclude;
        const limit = Number(this.dataset.limit) || 4;
        const handles = readRecentProducts().filter((h) => h && h !== exclude).slice(0, limit);
        const clear = qs('[data-recent-clear]', this);
        if (clear) clear.addEventListener('click', () => {
          try { localStorage.removeItem(RECENT_PRODUCTS); } catch (e) {}
          this.closest('section').hidden = true;
        });
        if (handles.length) this.load(handles);
      }
      async load(handles) {
        const root = (routes.root || '/').replace(/\/$/, '');
        const cards = await Promise.all(handles.map(async (h) => {
          try {
            const res = await fetch(`${root}/products/${encodeURIComponent(h)}?section_id=product-card-ajax`);
            if (!res.ok) return null;
            const doc = parseHTML(await res.text());
            const card = qs('.card', doc);
            return card ? card.outerHTML : null;
          } catch (e) { return null; }
        }));
        const found = cards.filter(Boolean);
        if (!found.length) return;
        qs('[data-recent-grid]', this).innerHTML = found.join('');
        this.closest('section').hidden = false;
      }
    });
  }

  /* Quick reorder ----------------------------------------------------------- */
  if (!customElements.get('quick-reorder')) {
    customElements.define('quick-reorder', class extends HTMLElement {
      connectedCallback() {
        this.all = qs('[data-reorder-all]', this);
        this.addEventListener('change', (e) => {
          if (e.target === this.all) qsa('[data-reorder-item]', this).forEach((c) => { c.checked = this.all.checked; });
          this.update();
        });
        qs('[data-reorder-add]', this).addEventListener('click', (e) => this.add(e.currentTarget));
        this.update();
      }
      picked() { return qsa('[data-reorder-item]:checked', this); }
      update() {
        const items = qsa('[data-reorder-item]', this);
        const picked = this.picked();
        if (this.all) this.all.checked = picked.length === items.length;
        let qty = 0;
        picked.forEach((c) => { qty += Math.max(1, Number(qs('[data-reorder-qty]', c.closest('li')).value) || 1); });
        qs('[data-reorder-count]', this).textContent = theme.strings.reorderCount.split('[count]').join(qty);
        qs('[data-reorder-add]', this).disabled = picked.length === 0;
        items.forEach((c) => c.closest('li').classList.toggle('is-off', !c.checked));
      }
      async add(btn) {
        const fd = new FormData();
        this.picked().forEach((c, i) => {
          fd.append(`items[${i}][id]`, c.value);
          fd.append(`items[${i}][quantity]`, String(Math.max(1, Number(qs('[data-reorder-qty]', c.closest('li')).value) || 1)));
        });
        btn.classList.add('is-loading');
        try { await theme.cart.add(fd); }
        catch (err) { theme.toast(err.message || theme.strings.error); }
        finally { btn.classList.remove('is-loading'); }
      }
    });
  }

  /* Collection guide: Read more ------------------------------------------- */
  if (!customElements.get('collection-guide')) {
    customElements.define('collection-guide', class extends HTMLElement {
      connectedCallback() {
        this.content = qs('[data-guide-content]', this);
        this.toggle = qs('[data-guide-toggle]', this);
        if (!this.toggle || !this.content) return;
        const limit = parseInt(getComputedStyle(this).getPropertyValue('--guide-height'), 10) || 260;
        if (this.content.scrollHeight <= limit + 80) return;
        this.classList.add('is-collapsed');
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => this.set(this.classList.contains('is-collapsed')));
        const openFromHash = () => { if (window.location.hash === '#CollectionGuide') this.set(true); };
        window.addEventListener('hashchange', openFromHash);
        openFromHash();
      }
      set(open) {
        this.classList.toggle('is-collapsed', !open);
        this.toggle.setAttribute('aria-expanded', String(open));
        qs('[data-more]', this.toggle).hidden = open;
        qs('[data-less]', this.toggle).hidden = !open;
        if (!open) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* Collection range guide: collapsed with Learn more ------------------------ */
  if (!customElements.get('range-guide')) {
    customElements.define('range-guide', class extends HTMLElement {
      connectedCallback() {
        this.toggle = qs('[data-rg-toggle]', this);
        if (!this.toggle) return;
        this.toggle.addEventListener('click', () => this.set(this.classList.contains('is-collapsed')));
        const fromHash = () => { if (window.location.hash === `#${this.dataset.anchor}`) this.set(true, false); };
        window.addEventListener('hashchange', fromHash);
        document.addEventListener('click', (e) => {
          const a = e.target.closest(`a[href$="#${this.dataset.anchor}"]`);
          if (a) this.set(true, false);
        });
        fromHash();
      }
      set(open, scroll = true) {
        this.classList.toggle('is-collapsed', !open);
        this.toggle.setAttribute('aria-expanded', String(open));
        qs('[data-more]', this.toggle).hidden = open;
        qs('[data-less]', this.toggle).hidden = !open;
        if (!open && scroll) this.closest('section').scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* Discount promo bar: copy code, close ------------------------------------- */
  if (!customElements.get('promo-bar')) {
    customElements.define('promo-bar', class extends HTMLElement {
      connectedCallback() {
        const copy = qs('[data-promo-copy]', this);
        if (copy) copy.addEventListener('click', async () => {
          const code = copy.dataset.promoCopy;
          try { await navigator.clipboard.writeText(code); } catch (e) {
            const t = document.createElement('textarea'); t.value = code; document.body.appendChild(t); t.select();
            try { document.execCommand('copy'); } catch (err) {} t.remove();
          }
          const hint = qs('[data-promo-hint]', copy);
          if (hint) { const was = hint.textContent; hint.textContent = theme.strings.promoCopied; setTimeout(() => { hint.textContent = was; }, 2000); }
          copy.classList.add('is-copied'); setTimeout(() => copy.classList.remove('is-copied'), 2000);
        });
        const close = qs('[data-promo-close]', this);
        if (close) close.addEventListener('click', () => {
          try { localStorage.setItem(`cl-promo-${this.dataset.key}`, '1'); } catch (e) {}
          this.hidden = true;
        });
      }
    });
  }

  /* Product image zoom: full-screen viewer with click / double-tap to zoom ---- */
  (() => {
    let box; let items = []; let idx = 0;
    const build = () => {
      box = document.createElement('dialog');
      box.className = 'lightbox';
      box.setAttribute('aria-label', theme.strings.zoomTitle || 'Image viewer');
      box.innerHTML = `
        <div class="lightbox__stage" data-lb-stage><img class="lightbox__img" alt="" data-lb-img></div>
        <button type="button" class="lightbox__btn lightbox__close" data-lb-close aria-label="${theme.strings.close || 'Close'}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>
        <button type="button" class="lightbox__btn lightbox__prev" data-lb-prev aria-label="${theme.strings.previous || 'Previous'}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg></button>
        <button type="button" class="lightbox__btn lightbox__next" data-lb-next aria-label="${theme.strings.next || 'Next'}"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg></button>
        <div class="lightbox__bar"><span data-lb-count></span><span class="lightbox__hint" data-lb-hint></span></div>`;
      document.body.appendChild(box);
      const stage = qs('[data-lb-stage]', box); const img = qs('[data-lb-img]', box);
      const isTouch = () => window.matchMedia('(hover: none)').matches;
      const hint = () => { qs('[data-lb-hint]', box).textContent = box.classList.contains('is-zoomed') ? (theme.strings.zoomOut || '') : (isTouch() ? theme.strings.zoomHintTouch : theme.strings.zoomHint) || ''; };
      const setZoom = (on, x, y) => {
        box.classList.toggle('is-zoomed', on);
        if (on) {
          const scale = 2.5;
          const r = img.getBoundingClientRect();
          const fx = r.width ? (x - r.left) / r.width : 0.5; const fy = r.height ? (y - r.top) / r.height : 0.5;
          img.style.width = `${Math.max(stage.clientWidth * scale, 1)}px`;
          requestAnimationFrame(() => {
            stage.scrollLeft = fx * img.offsetWidth - stage.clientWidth / 2;
            stage.scrollTop = fy * img.offsetHeight - stage.clientHeight / 2;
          });
        } else { img.style.width = ''; stage.scrollTo(0, 0); }
        hint();
      };
      const show = (i) => {
        idx = (i + items.length) % items.length;
        setZoom(false);
        img.src = items[idx].src; img.alt = items[idx].alt || '';
        qs('[data-lb-count]', box).textContent = items.length > 1 ? `${idx + 1} / ${items.length}` : '';
        box.classList.toggle('is-single', items.length < 2);
      };
      box.show_ = show;
      qs('[data-lb-close]', box).addEventListener('click', () => box.close());
      qs('[data-lb-prev]', box).addEventListener('click', () => show(idx - 1));
      qs('[data-lb-next]', box).addEventListener('click', () => show(idx + 1));
      box.addEventListener('close', () => { setZoom(false); document.documentElement.classList.remove('lightbox-open'); });
      box.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') show(idx - 1);
        if (e.key === 'ArrowRight') show(idx + 1);
      });
      // Mouse: click to zoom in/out, move to look around while zoomed.
      let lastTap = 0; let downX = 0; let downY = 0; let moved = false;
      stage.addEventListener('pointerdown', (e) => { downX = e.clientX; downY = e.clientY; moved = false; });
      stage.addEventListener('pointermove', (e) => {
        if (Math.abs(e.clientX - downX) > 8 || Math.abs(e.clientY - downY) > 8) moved = true;
        if (e.pointerType === 'mouse' && box.classList.contains('is-zoomed')) {
          const r = stage.getBoundingClientRect();
          stage.scrollLeft = ((e.clientX - r.left) / r.width) * (stage.scrollWidth - stage.clientWidth);
          stage.scrollTop = ((e.clientY - r.top) / r.height) * (stage.scrollHeight - stage.clientHeight);
        }
      });
      stage.addEventListener('pointerup', (e) => {
        const zoomed = box.classList.contains('is-zoomed');
        if (e.pointerType === 'mouse') {
          if (e.target === img || zoomed) setZoom(!zoomed, e.clientX, e.clientY);
          else box.close();
          return;
        }
        // Touch: swipe to change image, double-tap to zoom.
        const dx = e.clientX - downX;
        if (!zoomed && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(e.clientY - downY)) { show(idx + (dx < 0 ? 1 : -1)); return; }
        if (moved) return;
        const now = Date.now();
        if (now - lastTap < 320) { setZoom(!zoomed, e.clientX, e.clientY); lastTap = 0; } else lastTap = now;
      });
      box.hint_ = hint;
    };
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-zoom]');
      if (!btn) return;
      const gallery = btn.closest('product-gallery') || document;
      const all = qsa('[data-zoom]', gallery);
      items = all.map((b) => ({ src: b.dataset.zoom, alt: b.dataset.zoomAlt }));
      if (!box) build();
      box.show_(all.indexOf(btn));
      document.documentElement.classList.add('lightbox-open');
      box.showModal();
      box.hint_();
    });
  })();

  /* Contact page: pre-select a topic from ?topic=… ------------------------- */
  (() => {
    const topic = new URLSearchParams(window.location.search).get('topic');
    if (!topic) return;
    const input = qs(`[data-topic="${CSS.escape(topic.toLowerCase())}"]`);
    if (input) input.checked = true;
  })();

  /* Guide contents list from H2 headings --------------------------------- */
  (() => {
    const body = qs('[data-guide-body]');
    const toc = qs('[data-guide-toc]');
    if (!body || !toc) return;
    const heads = qsa('h2', body);
    if (heads.length < 2) return;
    const list = qs('[data-guide-toc-list]', toc);
    const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
    const links = heads.map((h, i) => {
      if (!h.id) h.id = slug(h.textContent) || `section-${i + 1}`;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = h.textContent;
      li.appendChild(a);
      list.appendChild(li);
      return a;
    });
    toc.hidden = false;
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          links.forEach((a) => a.classList.toggle('is-active', a.hash === `#${en.target.id}`));
        });
      }, { rootMargin: '0px 0px -70% 0px' });
      heads.forEach((h) => io.observe(h));
    }
  })();

  /* Tidy search-tracking parameters from the address bar ------------------ */
  // Shopify adds _pos, _psq, _ss, _sid/_psid to search result links for its search analytics.
  // Once the page has loaded (and Shopify has recorded the click), remove them so the
  // URL customers see and share is clean. Other parameters such as ?variant= are kept.
  (() => {
    const TRACKING = ['_pos', '_psq', '_ss', '_sid', '_psid', '_fid'];
    const url = new URL(window.location.href);
    if (!TRACKING.some((k) => url.searchParams.has(k))) return;
    const tidy = () => {
      TRACKING.forEach((k) => url.searchParams.delete(k));
      const clean = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
      try { window.history.replaceState(window.history.state, '', clean); } catch (e) {}
    };
    if (document.readyState === 'complete') setTimeout(tidy, 1500);
    else window.addEventListener('load', () => setTimeout(tidy, 1500), { once: true });
  })();

  /* Country / language picker ---------------------------------------------- */
  if (!customElements.get('localization-picker')) {
    customElements.define('localization-picker', class extends HTMLElement {
      connectedCallback() {
        this.button = qs('.loc-picker__btn', this);
        this.panel = qs('.loc-picker__panel', this);
        this.input = qs('input[type="hidden"]', this);
        this.filter = qs('[data-loc-filter]', this);
        this.button.addEventListener('click', () => (this.panel.hidden ? this.show() : this.hide()));
        this.addEventListener('click', (e) => {
          const opt = e.target.closest('.loc-picker__option');
          if (!opt) return;
          if (opt.getAttribute('aria-current') === 'true') { this.hide(true); return; }
          this.input.value = opt.dataset.value;
          this.button.classList.add('is-loading');
          this.closest('form').submit();
        });
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.panel.hidden) { e.stopPropagation(); this.hide(true); }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const opts = qsa('.loc-picker__option', this.panel).filter((o) => !o.closest('li').hidden);
            if (!opts.length || this.panel.hidden) return;
            e.preventDefault();
            const i = opts.indexOf(document.activeElement);
            const next = e.key === 'ArrowDown' ? (i + 1) % opts.length : (i <= 0 ? opts.length - 1 : i - 1);
            opts[next].focus();
          }
        });
        this.addEventListener('focusout', (e) => { if (e.relatedTarget && !this.contains(e.relatedTarget)) this.hide(); });
        document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.hide(); });
        if (this.filter) this.filter.addEventListener('input', () => this.applyFilter());
      }
      show() {
        qsa('localization-picker').forEach((p) => p !== this && p.hide && p.hide());
        this.panel.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
        this.panel.classList.toggle('loc-picker__panel--left', this.button.getBoundingClientRect().left < 300);
        const current = qs('[aria-current="true"]', this.panel);
        if (current) current.scrollIntoView({ block: 'center' });
        (this.filter || current || qs('.loc-picker__option', this.panel)).focus({ preventScroll: true });
      }
      hide(refocus) {
        if (this.panel.hidden) return;
        this.panel.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
        if (this.filter && this.filter.value) { this.filter.value = ''; this.applyFilter(); }
        if (refocus) this.button.focus();
      }
      applyFilter() {
        const q = this.filter.value.trim().toLowerCase();
        let any = false;
        qsa('.loc-picker__option', this.panel).forEach((o) => {
          const hit = !q || o.dataset.search.includes(q);
          o.closest('li').hidden = !hit;
          if (hit) any = true;
        });
        const empty = qs('[data-loc-empty]', this.panel);
        if (empty) empty.hidden = any;
      }
    });
  }

  /* Sticky header offset --------------------------------------------------- */
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', class extends HTMLElement {
      connectedCallback() {
        const set = () => document.documentElement.style.setProperty('--header-offset', this.classList.contains('is-sticky') ? `${this.offsetHeight}px` : '0px');
        set();
        new ResizeObserver(set).observe(this);
      }
    });
  }

  /* Mega menu -------------------------------------------------------------- */
  if (!customElements.get('mega-menu')) {
    customElements.define('mega-menu', class extends HTMLElement {
      connectedCallback() {
        this.triggers = qsa('[data-mega-trigger]', this);
        // Keep the menu on one line as items are added: tighten the gaps, then the text size, then wrap.
        this.nav = this.closest('.nav');
        this.list = qs('.nav__list', this);
        if (this.nav && this.list && 'ResizeObserver' in window) {
          let raf;
          new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.fit()); }).observe(this);
          this.fit();
        }
        const hover = window.matchMedia('(hover: hover) and (min-width: 990px)');
        this.triggers.forEach((btn) => {
          const panel = document.getElementById(btn.getAttribute('aria-controls'));
          const item = btn.closest('.nav__item');
          btn.addEventListener('click', () => this.toggle(btn, panel));
          if (hover.matches) {
            let t;
            item.addEventListener('mouseenter', () => { clearTimeout(t); t = setTimeout(() => this.show(btn, panel), 120); });
            item.addEventListener('mouseleave', () => { clearTimeout(t); t = setTimeout(() => this.hide(btn, panel), 180); });
          }
        });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.hideAll(true); });
        document.addEventListener('click', (e) => { if (!this.closest('.nav').contains(e.target)) this.hideAll(); });
        this.addEventListener('focusout', (e) => { if (e.relatedTarget && !this.contains(e.relatedTarget)) this.hideAll(); });
      }
      wraps() {
        const items = qsa(':scope > .nav__item', this.list);
        return items.length > 1 && items[items.length - 1].offsetTop > items[0].offsetTop + 4;
      }
      fit() {
        // Shrink the gaps first, then the text (down to 11px), until every item fits on one row.
        const css = getComputedStyle(this.nav);
        let gap = parseFloat(css.getPropertyValue('--menu-gap')) || 28;
        let size = parseFloat(css.getPropertyValue('--menu-fs')) || 14;
        const set = () => { this.nav.style.setProperty('--nav-gap', `${gap}px`); this.nav.style.setProperty('--nav-fs', `${size}px`); };
        set();
        let guard = 0;
        while (this.wraps() && guard < 60) {
          guard += 1;
          if (gap > 14) gap = Math.max(14, gap - 2);
          else if (size > 11) size = Math.max(11, Math.round((size - 0.25) * 4) / 4);
          else if (gap > 6) gap -= 1;
          else if (size > 10) size = Math.max(10, Math.round((size - 0.25) * 4) / 4);
          else break;
          set();
        }
      }
      show(btn, panel) {
        this.hideAll();
        btn.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        panel.classList.remove('dropdown--align-right', 'dropdown--flip-sub');
        const rect = panel.getBoundingClientRect();
        if (rect.right > window.innerWidth - 16) panel.classList.add('dropdown--align-right');
        const after = panel.getBoundingClientRect();
        if (after.right + 260 > window.innerWidth - 16) panel.classList.add('dropdown--flip-sub');
      }
      hide(btn, panel) { btn.setAttribute('aria-expanded', 'false'); panel.hidden = true; }
      toggle(btn, panel) { btn.getAttribute('aria-expanded') === 'true' ? this.hide(btn, panel) : this.show(btn, panel); }
      hideAll(refocus) {
        this.triggers.forEach((b) => {
          if (b.getAttribute('aria-expanded') === 'true' && refocus) b.focus();
          this.hide(b, document.getElementById(b.getAttribute('aria-controls')));
        });
      }
    });
  }

  /* Cart ------------------------------------------------------------------- */
  const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  theme.cart = {
    sectionIds() {
      const ids = qs('cart-drawer') ? [...CART_SECTIONS] : ['cart-icon-bubble'];
      if (qs('cart-notification')) ids.push('cart-notification');
      qsa('[data-cart-section]').forEach((el) => ids.push(el.dataset.cartSection));
      return ids;
    },
    async add(formData, { openDrawer = true } = {}) {
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      if (openDrawer) {
        const popup = qs('cart-notification');
        const drawer = qs('cart-drawer');
        if (theme.cartType !== 'page' && popup) popup.show(data);
        else if (theme.cartType !== 'page' && drawer) drawer.open();
        else { window.location.href = routes.cart; return data; }
      }
      return data;
    },
    async change(line, quantity) {
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    render(sections) {
      if (!sections) return;
      const active = document.activeElement;
      const activeLine = active && active.dataset ? active.dataset.line : null;
      const activeInDrawer = active && active.closest && active.closest('cart-drawer');
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-icon-bubble') {
          const fresh = qs('#cart-icon-bubble', doc);
          const current = qs('#cart-icon-bubble');
          if (fresh && current) { current.innerHTML = fresh.innerHTML; current.setAttribute('aria-label', fresh.getAttribute('aria-label')); }
          return;
        }
        qsa('[data-cart-replace]', doc).forEach((fresh) => {
          const current = qs(`[data-cart-replace="${fresh.dataset.cartReplace}"]`);
          if (current) current.replaceWith(fresh);
        });
      });
      if (activeLine || activeInDrawer) {
        const again = activeLine && qs(`[data-line="${activeLine}"]`);
        const fallback = qs('cart-drawer.is-open .drawer__panel');
        const target = again || fallback;
        if (target && document.activeElement === document.body) target.focus();
      }
    }
  };

  /* Cart discount codes ------------------------------------------------------ */
  theme.cart.discount = async (codes) => {
    const res = await fetch(`${routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), sections: theme.cart.sectionIds(), sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
    return data;
  };
  const appliedCodes = () => [...new Set(qsa('[data-applied-code]').map((el) => el.dataset.appliedCode))];
  const discountMessage = (text, ok) => {
    qsa('[data-discount-msg]').forEach((el) => {
      el.textContent = text;
      el.hidden = !text;
      el.classList.toggle('is-ok', !!ok);
    });
  };
  const runDiscount = async (codes, btn, added) => {
    if (btn) btn.classList.add('is-loading');
    try {
      let data = await theme.cart.discount(codes);
      const entry = added && (data.discount_codes || []).find((d) => d.code.toLowerCase() === added.toLowerCase());
      if (added && (!entry || !entry.applicable)) {
        // Not valid for this cart: take it straight back off so it isn't left hanging.
        data = await theme.cart.discount(codes.filter((c) => c.toLowerCase() !== added.toLowerCase()));
        theme.cart.render(data.sections);
        discountMessage(theme.strings.discountInvalid.split('[code]').join(added.toUpperCase()), false);
      } else {
        theme.cart.render(data.sections);
        discountMessage(added ? theme.strings.discountApplied.split('[code]').join(added.toUpperCase()) : '', true);
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (err) {
      discountMessage(err.message || theme.strings.error, false);
    } finally {
      if (btn) btn.classList.remove('is-loading');
    }
  };
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input');
    const code = input.value.trim();
    if (!code) { discountMessage(theme.strings.discountEmpty, false); input.focus(); return; }
    const codes = appliedCodes().filter((c) => c.toLowerCase() !== code.toLowerCase()).concat(code);
    runDiscount(codes, form.querySelector('button'), code);
  });
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-discount-remove]');
    if (!rm) return;
    e.preventDefault();
    const code = rm.dataset.discountRemove;
    runDiscount(appliedCodes().filter((c) => c !== code), null, null);
  });

  /* Cart page: sticky checkout bar on mobile while the main button is off-screen */
  if (!customElements.get('cart-sticky')) {
    customElements.define('cart-sticky', class extends HTMLElement {
      connectedCallback() {
        const main = document.querySelector('[data-cart-checkout-main]');
        if (!main || !('IntersectionObserver' in window)) return;
        this.io = new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top > 0;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
          this.querySelector('button').tabIndex = show ? 0 : -1;
        });
        this.io.observe(main);
      }
      disconnectedCallback() { if (this.io) this.io.disconnect(); }
    });
  }

  /* Delivery cost checker (cart page + drawer) ------------------------------ */
  if (!customElements.get('shipping-estimator')) {
    const SHIP_KEY = 'cl-ship';
    const cartRoot = (routes.cartUpdate || '/cart/update').replace(/update$/, '');
    const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    customElements.define('shipping-estimator', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-ship-form]');
        this.results = this.querySelector('[data-ship-results]');
        this.details = this.querySelector('details');
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(SHIP_KEY)); } catch (e) {}
        if (saved && saved.zip) {
          this.form.elements.zip.value = saved.zip;
          const c = this.form.elements.country;
          if (c && c.tagName === 'SELECT' && [...c.options].some((o) => o.value === saved.country)) c.value = saved.country;
        }
        this.form.addEventListener('submit', (e) => { e.preventDefault(); this.check(); });
        // Re-check automatically for a saved postcode, as rates depend on what's in the cart.
        if (saved && saved.zip) {
          if (this.details.open && this.offsetParent !== null) this.check();
          else this.details.addEventListener('toggle', () => { if (this.details.open && !this.checked) this.check(); }, { once: true });
        }
      }
      async check() {
        const s = theme.strings.shipping;
        const zip = this.form.elements.zip.value.trim().toUpperCase();
        const country = this.form.elements.country.value;
        if (!zip) { this.show(`<p class="ship-est__msg is-error">${esc(s.empty)}</p>`); return; }
        this.checked = true;
        try { localStorage.setItem(SHIP_KEY, JSON.stringify({ zip, country })); } catch (e) {}
        const btn = this.form.querySelector('button');
        btn.classList.add('is-loading');
        this.show(`<p class="ship-est__msg">${esc(s.checking)}</p>`);
        const q = new URLSearchParams({ 'shipping_address[zip]': zip, 'shipping_address[country]': country, 'shipping_address[province]': '' });
        try {
          const prep = await fetch(`${cartRoot}prepare_shipping_rates.json?${q}`, { method: 'POST', headers: { Accept: 'application/json' } });
          if (!prep.ok) throw new Error(s.error);
          let rates = null;
          for (let i = 0; i < 20 && !rates; i += 1) {
            const res = await fetch(`${cartRoot}async_shipping_rates.json?${q}`, { headers: { Accept: 'application/json' } });
            if (res.status === 200) { const data = await res.json(); if (data && data.shipping_rates) rates = data.shipping_rates; }
            else if (res.status !== 202) throw new Error(s.error);
            if (!rates) await sleep(500);
          }
          if (!rates) throw new Error(s.error);
          this.render(rates, zip);
        } catch (e) {
          this.show(`<p class="ship-est__msg is-error">${esc(e.message || s.error)}</p>`);
        } finally {
          btn.classList.remove('is-loading');
        }
      }
      render(rates, zip) {
        const s = theme.strings.shipping;
        if (!rates.length) { this.show(`<p class="ship-est__msg is-error">${esc(s.none)}</p>`); return; }
        const rate = Number(this.dataset.vatRate) || 0;
        const rows = rates.slice().sort((a, b) => parseFloat(a.price) - parseFloat(b.price)).map((r) => {
          const cents = Math.round(parseFloat(r.price) * 100);
          const days = Array.isArray(r.delivery_days) ? r.delivery_days : [];
          let when = '';
          if (days && days.length === 2 && days[0] != null) when = days[0] === days[1] ? (Number(days[0]) === 1 ? s.daysSingle : s.daysOne).split('[min]').join(days[0]) : s.daysRange.split('[min]').join(days[0]).split('[max]').join(days[1]);
          const price = cents === 0 ? esc(s.free) : esc(theme.formatMoney(cents));
          const ex = cents > 0 && rate ? `<span class="ship-est__ex">${esc(s.exVat.split('[amount]').join(theme.formatMoney(Math.round((cents * 100) / (100 + rate)))))}</span>` : '';
          return `<li class="ship-est__rate"><span class="ship-est__name">${esc(r.presentment_name || r.name)}${when ? `<span class="ship-est__when">${esc(when)}</span>` : ''}</span><span class="ship-est__price"><strong>${price}</strong>${ex}</span></li>`;
        }).join('');
        this.show(`<p class="ship-est__intro">${esc(s.intro.split('[zip]').join(zip))}</p><ul class="ship-est__rates" role="list">${rows}</ul>`);
      }
      show(html) { this.results.innerHTML = html; }
    });
  }

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-open]');
    const drawer = qs('cart-drawer');
    if (!link || !drawer || theme.cartType !== 'drawer') return;
    e.preventDefault();
    drawer.open(link);
  });

  /* Quantity input --------------------------------------------------------- */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class extends HTMLElement {
      connectedCallback() {
        this.input = qs('input', this);
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('button[name]');
          if (!btn) return;
          e.preventDefault();
          const step = Number(this.input.step) || 1;
          const min = Number(this.input.min) || 0;
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const val = Number(this.input.value) || 0;
          const next = btn.name === 'plus' ? Math.min(val + step, max) : Math.max(val - step, min);
          if (next === val) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  /* Cart line items (drawer + cart page) ----------------------------------- */
  if (!customElements.get('cart-items')) {
    customElements.define('cart-items', class extends HTMLElement {
      connectedCallback() {
        this.timers = new Map();
        const update = (input) => {
          const line = input.dataset.line;
          clearTimeout(this.timers.get(line));
          this.timers.set(line, setTimeout(() => { this.timers.delete(line); this.update(line, Number(input.value)); }, 300));
        };
        this.addEventListener('change', (e) => { if (e.target.matches('[data-line]')) update(e.target); });
        this.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target.matches('[data-line]')) { e.preventDefault(); clearTimeout(this.timers.get(e.target.dataset.line)); this.update(e.target.dataset.line, Number(e.target.value)); } });
        this.addEventListener('click', (e) => {
          const remove = e.target.closest('[data-remove-line]');
          if (!remove) return;
          e.preventDefault();
          this.update(remove.dataset.removeLine, 0);
        });
      }
      async update(line, quantity) {
        this.classList.add('is-loading');
        try { await theme.cart.change(Number(line), quantity); }
        catch (err) { theme.toast(err.message); }
        finally { this.classList.remove('is-loading'); }
      }
    });
  }

  /* Product form ----------------------------------------------------------- */
  if (!customElements.get('product-form')) {
    customElements.define('product-form', class extends HTMLElement {
      connectedCallback() {
        this.form = qs('form', this);
        this.button = qs('[type="submit"]', this.form);
        this.error = qs('[data-form-error]', this);
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
      }
      async onSubmit(e) {
        e.preventDefault();
        this.button = qs('[type="submit"]', this.form);
        if (this.button.getAttribute('aria-disabled') === 'true') return;
        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-disabled', 'true');
        if (this.error) this.error.hidden = true;
        try {
          await theme.cart.add(new FormData(this.form));
        } catch (err) {
          if (this.error) { this.error.textContent = err.message; this.error.hidden = false; }
          else theme.toast(err.message);
        } finally {
          this.button.classList.remove('is-loading');
          this.button.removeAttribute('aria-disabled');
        }
      }
    });
  }

  /* Variant picker --------------------------------------------------------- */
  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', class extends HTMLElement {
      connectedCallback() {
        const json = qs('script[type="application/json"]', this);
        this.variants = json ? JSON.parse(json.textContent) : [];
        this.sectionId = this.dataset.section;
        this.url = this.dataset.url;
        this.addEventListener('change', () => this.onChange());
        this.markAvailability();
      }
      selected() {
        return qsa('fieldset, .variant-select', this).map((group) => {
          const checked = qs('input:checked', group);
          const select = qs('select', group);
          return checked ? checked.value : select ? select.value : null;
        });
      }
      onChange() {
        const opts = this.selected();
        const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
        this.markAvailability();
        const idInputs = qsa(`[data-variant-id="${this.sectionId}"]`);
        if (!variant) {
          this.setButton(false, theme.strings.unavailable);
          return;
        }
        idInputs.forEach((i) => { i.value = variant.id; i.dispatchEvent(new Event('change', { bubbles: true })); });
        window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);
        if (variant.featured_media) document.dispatchEvent(new CustomEvent('variant:media', { detail: { section: this.sectionId, mediaId: variant.featured_media.id } }));
        this.refresh(variant.id);
      }
      markAvailability() {
        const sel = this.selected();
        qsa('fieldset', this).forEach((group, gi) => {
          qsa('input', group).forEach((input) => {
            const candidate = [...sel];
            candidate[gi] = input.value;
            const ok = this.variants.some((v) => v.available && v.options.every((o, i) => i === gi ? o === input.value : (i < gi ? o === candidate[i] : true)));
            input.classList.toggle('is-unavailable', !ok);
          });
          const legendValue = qs('[data-selected-value]', group);
          const checked = qs('input:checked', group);
          if (legendValue && checked) legendValue.textContent = checked.value;
        });
      }
      setButton(enabled, label) {
        qsa(`[data-buy-button="${this.sectionId}"]`).forEach((btn) => {
          btn.disabled = !enabled;
          const span = qs('[data-label]', btn);
          if (span && label) span.textContent = label;
        });
      }
      async refresh(variantId) {
        if (this.refreshController) this.refreshController.abort();
        this.refreshController = new AbortController();
        this.currentVariant = variantId;
        let doc;
        try {
          const res = await fetch(`${this.url}?variant=${variantId}&section_id=${this.sectionId}`, { signal: this.refreshController.signal });
          doc = parseHTML(await res.text());
        } catch (err) { return; }
        if (this.currentVariant !== variantId) return;
        qsa('[data-variant-update]', doc).forEach((fresh) => {
          const key = fresh.dataset.variantUpdate;
          qsa(`[data-variant-update="${key}"]`).forEach((current) => {
            if (current.closest('variant-picker') && key !== 'buttons') return;
            current.innerHTML = fresh.innerHTML;
            if (fresh.hasAttribute('disabled')) current.setAttribute('disabled', ''); else current.removeAttribute('disabled');
          });
        });
      }
    });
  }

  /* Product gallery -------------------------------------------------------- */
  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', class extends HTMLElement {
      connectedCallback() {
        this.track = qs('.gallery__slides', this);
        this.slides = qsa('.gallery__slide', this);
        this.thumbs = qsa('.gallery__thumb', this);
        this.counter = qs('[data-gallery-counter]', this);
        if (!this.track) return;
        this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));
        qsa('[data-gallery-prev]', this).forEach((b) => b.addEventListener('click', () => this.goTo(this.index - 1)));
        qsa('[data-gallery-next]', this).forEach((b) => b.addEventListener('click', () => this.goTo(this.index + 1)));
        this.index = 0;
        this.track.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
        document.addEventListener('variant:media', (e) => {
          if (e.detail.section !== this.dataset.section) return;
          const i = this.slides.findIndex((s) => s.dataset.mediaId === String(e.detail.mediaId));
          if (i > -1) this.goTo(i);
        });
      }
      goTo(i) {
        const n = this.slides.length;
        this.index = (i + n) % n;
        const laidOut = (this.classList.contains('gallery--stacked') || this.classList.contains('gallery--grid')) && window.matchMedia('(min-width: 990px)').matches;
        if (laidOut) this.slides[this.index].scrollIntoView({ behavior: 'smooth', block: 'center' });
        else this.track.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: 'smooth' });
        this.update();
      }
      sync() {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.index) { this.index = i; this.update(); }
      }
      update() {
        this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
        if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
        this.slides.forEach((s, i) => { const v = qs('video', s); if (v && i !== this.index) v.pause(); });
      }
    });
  }

  /* Tabs ------------------------------------------------------------------- */
  if (!customElements.get('product-tabs')) {
    customElements.define('product-tabs', class extends HTMLElement {
      connectedCallback() {
        this.tabs = qsa('[role="tab"]', this);
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              e.preventDefault();
              const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
              this.select(next); this.tabs[next].focus();
            }
          });
        });
      }
      select(index) {
        this.tabs.forEach((t, i) => {
          const on = i === index;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
      }
    });
  }

  /* Sticky add to cart ----------------------------------------------------- */
  if (!customElements.get('sticky-atc')) {
    customElements.define('sticky-atc', class extends HTMLElement {
      connectedCallback() {
        const target = document.getElementById(this.dataset.target);
        if (!target || !('IntersectionObserver' in window)) return;
        new IntersectionObserver(([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', passed);
          this.setAttribute('aria-hidden', String(!passed));
        }).observe(target);
        const btn = qs('[data-sticky-submit]', this);
        if (btn) btn.addEventListener('click', () => { const form = target.closest('form'); if (form && !btn.disabled) form.requestSubmit(); });
      }
    });
  }

  /* Collection filters ----------------------------------------------------- */
  if (!customElements.get('collection-filters')) {
    customElements.define('collection-filters', class extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.form = qs('form[data-filter-form]', this);
        const go = debounce(() => this.apply(this.formUrl()), 350);
        this.addEventListener('change', (e) => {
          if (e.target.closest('form[data-filter-form]') || e.target.matches('[data-sort]')) {
            if (e.target.matches('[data-price]')) go(); else this.apply(this.formUrl());
          }
        });
        this.addEventListener('input', (e) => { if (e.target.matches('[data-price]')) go(); });
        this.addEventListener('click', (e) => {
          const link = e.target.closest('[data-filter-link]');
          if (link) { e.preventDefault(); this.apply(new URL(link.href, window.location.origin).search.slice(1)); }
          const more = e.target.closest('[data-load-more]');
          if (more) { e.preventDefault(); this.loadMore(more); }
        });
        window.addEventListener('popstate', () => this.apply(window.location.search.slice(1), false));
        this.applyView();
      }
      applyView() {
        const view = this.view || this.dataset.defaultView || 'grid';
        const grid = qs('[data-product-grid]', this);
        if (grid) {
          const base = grid.dataset.cardStyle;
          ['standard', 'detailed', 'compact', 'list'].forEach((st) => grid.classList.remove(`product-grid--${st}`));
          grid.classList.add(`product-grid--${view === 'list' ? 'list' : (base === 'list' ? 'standard' : base)}`);
        }
        qsa('[data-view]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view)));
      }
      formUrl() {
        const params = this.form ? new URLSearchParams(new FormData(this.form)) : new URLSearchParams();
        const sort = qs('[data-sort]', this);
        if (sort) params.set('sort_by', sort.value);
        for (const [k, v] of Array.from(params.entries())) { if (v === '') params.delete(k); }
        return params.toString();
      }
      async apply(search, push = true) {
        this.classList.add('is-loading');
        try {
          const res = await fetch(`${window.location.pathname}?${search}${search ? '&' : ''}section_id=${this.sectionId}`);
          const doc = parseHTML(await res.text());
          qsa('[data-filter-replace]', doc).forEach((fresh) => {
            const current = qs(`[data-filter-replace="${fresh.dataset.filterReplace}"]`, this);
            if (!current) return;
            if (fresh.dataset.filterReplace === 'facets') this.mergeFacets(current, fresh); else current.replaceWith(fresh);
          });
          this.applyView();
          if (push) window.history.pushState({}, '', `${window.location.pathname}${search ? '?' + search : ''}`);
        } catch (err) {
          window.location.search = search;
        } finally {
          this.classList.remove('is-loading');
        }
      }
      mergeFacets(current, fresh) {
        const open = qsa('details[open]', current).map((d) => d.dataset.facet);
        const active = document.activeElement && current.contains(document.activeElement) ? document.activeElement.id : null;
        current.innerHTML = fresh.innerHTML;
        qsa('details', current).forEach((d) => { if (open.includes(d.dataset.facet)) d.open = true; });
        if (active && document.getElementById(active)) document.getElementById(active).focus();
        this.form = qs('form[data-filter-form]', this);
      }
      async loadMore(btn) {
        btn.classList.add('is-loading');
        let doc;
        try {
          const res = await fetch(`${btn.href}${btn.href.includes('?') ? '&' : '?'}section_id=${this.sectionId}`);
          doc = parseHTML(await res.text());
        } catch (err) { window.location.href = btn.href; return; }
        btn.classList.remove('is-loading');
        const grid = qs('[data-product-grid]', this);
        qsa('[data-product-grid] > *', doc).forEach((item) => grid.appendChild(item));
        const freshPager = qs('[data-filter-replace="pagination"]', doc);
        const pager = qs('[data-filter-replace="pagination"]', this);
        if (pager && freshPager) pager.replaceWith(freshPager);
        else if (pager) pager.innerHTML = '';
      }
    });
  }

  /* Product finder --------------------------------------------------------- */
  if (!customElements.get('product-finder')) {
    customElements.define('product-finder', class extends HTMLElement {
      connectedCallback() {
        this.answers = {};
        this.addEventListener('click', (e) => {
          const chip = e.target.closest('[data-finder-q]');
          if (!chip) return;
          const q = chip.dataset.finderQ;
          qsa(`[data-finder-q="${q}"]`, this).forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
          this.answers[q] = chip.dataset.value;
          this.update();
        });
        qsa('[aria-pressed="true"][data-finder-q]', this).forEach((c) => { this.answers[c.dataset.finderQ] = c.dataset.value; });
        this.update();
      }
      update() {
        const results = qsa('[data-finder-result]', this);
        let match = results.find((r) => r.dataset.a1 === this.answers['1'] && r.dataset.a2 === this.answers['2']);
        if (!match) match = results.find((r) => r.dataset.a1 === this.answers['1'] && !r.dataset.a2) || results.find((r) => !r.dataset.a1);
        results.forEach((r) => { r.hidden = r !== match; });
        const empty = qs('[data-finder-empty]', this);
        if (empty) empty.hidden = Boolean(match);
      }
    });
  }


  /* Bundle add (several products at once) ---------------------------------- */
  if (!customElements.get('bundle-add')) {
    customElements.define('bundle-add', class extends HTMLElement {
      connectedCallback() {
        qs('[data-bundle-submit]', this).addEventListener('click', async (e) => {
          const ids = qsa('[data-bundle-item]:checked', this).map((i) => i.value);
          if (!ids.length) return;
          const btn = e.currentTarget;
          const fd = new FormData();
          ids.forEach((id, i) => { fd.append(`items[${i}][id]`, id); fd.append(`items[${i}][quantity]`, '1'); });
          btn.classList.add('is-loading');
          try { await theme.cart.add(fd); } catch (err) { theme.toast(err.message); } finally { btn.classList.remove('is-loading'); }
        });
      }
    });
  }

  /* Share ------------------------------------------------------------------ */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    const data = { title: btn.dataset.title, url: btn.dataset.url };
    if (navigator.share) { try { await navigator.share(data); } catch (err) {} return; }
    try { await navigator.clipboard.writeText(data.url); theme.toast(theme.strings.linkCopied || 'Link copied'); } catch (err) {}
  });

  /* Product recommendations ------------------------------------------------ */
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        const load = async () => {
          try {
            const res = await fetch(this.dataset.url);
            const doc = parseHTML(await res.text());
            const fresh = qs('product-recommendations', doc);
            if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; this.hidden = false; }
            else this.closest('.shopify-section') && (this.closest('.shopify-section').hidden = true);
          } catch (err) {}
        };
        if (!('IntersectionObserver' in window)) { load(); return; }
        const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      }
    });
  }


  /* Made-to-order dispatch estimate ---------------------------------------- */
  if (!customElements.get('lead-time')) {
    customElements.define('lead-time', class extends HTMLElement {
      connectedCallback() {
        const out = qs('[data-estimate]', this);
        if (!out) return;
        const min = parseInt(this.dataset.min, 10);
        const max = parseInt(this.dataset.max, 10) || min;
        if (!min) return;
        const cfg = theme.madeToOrder || {};
        const closed = new Set((cfg.closed || []).map((d) => String(d).trim()).filter(Boolean));
        // Work in UK time so the estimate matches the factory's working day
        const parts = {};
        new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23' })
          .formatToParts(new Date()).forEach((p) => { parts[p.type] = parseInt(p.value, 10); });
        const ukNow = new Date(parts.year, parts.month - 1, parts.day, parts.hour || 0);
        if (isNaN(ukNow.getTime())) return;
        const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const isWorkingDay = (d) => d.getDay() !== 0 && d.getDay() !== 6 && !closed.has(key(d));
        const start = new Date(ukNow.getFullYear(), ukNow.getMonth(), ukNow.getDate());
        const cutoff = Number(cfg.cutoffHour);
        // Orders after the cut-off, or on a closed day, start counting from the next working day
        if (!isWorkingDay(start) || (cutoff && ukNow.getHours() >= cutoff)) {
          do { start.setDate(start.getDate() + 1); } while (!isWorkingDay(start));
        }
        const addWorkingDays = (from, n) => {
          const d = new Date(from);
          let added = 0;
          while (added < n) { d.setDate(d.getDate() + 1); if (isWorkingDay(d)) added += 1; }
          return d;
        };
        const first = addWorkingDays(start, min);
        const last = addWorkingDays(start, max);
        const fmt = (d, withMonth) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: withMonth ? 'short' : undefined });
        let text;
        if (key(first) === key(last)) text = fmt(first, true);
        else if (first.getMonth() === last.getMonth()) text = `${fmt(first, false)} – ${fmt(last, true)}`;
        else text = `${fmt(first, true)} – ${fmt(last, true)}`;
        qs('[data-date]', out).textContent = text;
        out.hidden = false;
      }
    });
  }

  /* Quick add on product cards -------------------------------------------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-card-add]');
    if (!form) return;
    e.preventDefault();
    const btn = qs('button', form);
    btn.classList.add('is-loading');
    try { await theme.cart.add(new FormData(form)); }
    catch (err) { theme.toast(err.message); }
    finally { btn.classList.remove('is-loading'); }
  });
})();
